import "./css/style.scss";
import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import {
  ButtonShapes,
  CampaignType,
  QuerySaveEmail,
  Offer,
  LayoutClass,
  QueryParams,
  SelectedOfferStorage,
  TemplateOptionSettings,
  Template,
  PlacementStage,
  TemplateIcons,
  ICONS_DEFAULT_VALUES,
  LayoutDevices,
} from "../../../types/global.types";
import {
  getTrackingEvent,
  postTagEmailOffer,
} from "../../../services/offers/offers.service";
import TemplateSingle from "../single/TemplateSingle.component";
import SwiperComponent from "./swiper/Swiper.component";
import PreCountAndSelectAll from "../components/PreCountAndSelectAll/PreCountAndSelectAll";
import TemplateFilters from "../components/Filters/desktop/TemplateFilters";
import RedirectionButton from "../components/Redirection/redirectionButton.component";
import FeaturedOfferSmall from "../components/FeaturedSlots/FeaturedOfferSmall/FeaturedOfferSmall";
import TemplateIcon from "../components/Icons/info.component";
import FeaturedOfferLarge from "../components/FeaturedSlots/FeaturedOfferLarge/FeaturedOfferLarge";
import { MultipleFeaturedWrapperDesktop } from "../components/FeaturedSlots/MultipleFeaturedWrapperDesktop/MultipleFeaturedWrapperDesktop";
import FooterText from "../components/FooterText/FooterText";
import MultipleFeaturedWrapperMobile from "../components/FeaturedSlots/MultipleFeaturedWrapperMobile/MultipleFeaturedWrapperMobile";
import EmailAutomaticTermsBanner from "../components/EmailAutomaticTermsBanner/EmailAutomaticTermsBanner.component";
import { getHeaderEmailAutomaticTermsText } from "../utils/emailAutomaticTerms.utils";
import { EMAIL_REGEX } from "../../../utility/regexp";
import { getStorageItem, updateStorageItem } from "../../../utility/storage";

interface Props {
  params: QueryParams;
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  campaignId: string;
  placementId: string;
  placementName: string;
  first: boolean;
  defaultOpen: boolean;
  campaignRes: CampaignType;
  type: LayoutClass;
  setOpen: (open: boolean) => void;
  selectedOffers: string[];
  buttonShape: ButtonShapes;
  email: string | undefined;
  slot: number;
  defaultSix: boolean;
  sessionId: string;
  layoutId: string;
  isExit: boolean;
  device: LayoutDevices;
  isSeen: boolean;
}

const PreviewPopupSuper = ({
  params,
  email,
  settings,
  campaignId,
  first,
  campaignRes,
  placementId,
  placementName,
  defaultOpen,
  setOpen,
  selectedOffers,
  buttonShape,
  settingsTemplate,
  slot,
  defaultSix,
  sessionId,
  layoutId,
  isExit,
  device,
  isSeen,
}: Props) => {
  const [isLoadMore, setLoadMore] = useState<boolean>(
    settings?.mainLayout?.startAsExpanded || false
  );

  const [campaign, setCampaign] = useState<CampaignType | undefined>(
    campaignRes
  );

  const [preEmail, setPreEmail] = useState<string | undefined>(email || "");

  const [single, setSingle] = useState<Offer | undefined>();
  const [isSingleSave, setSingleSave] = useState<boolean>(false);
  const [isSingleShare, setSingleShare] = useState<boolean>(false);

  const [displayOnly] = useState<boolean>(
    campaignRes?.placementSettings[0]?.displayOnly
  );
  const refOffers = useRef<HTMLDivElement | null>(null);

  const [hasOpen, setHasOpen] = useState<boolean>(!first);
  const [offerFilteredList, setOfferFilteredList] = useState<Offer[]>(
    campaignRes.offers
  );

  const [selectedEmailOfferId, setSelectedEmailOfferId] = useState<string>("");
  const [autoSendingOfferId, setAutoSendingOfferId] = useState<string>("");
  const [numberOfOfferColumns, setNumberOfOfferColumns] = useState<number>(
    Number(settings?.mainLayout.offersNumber) || 3
  );
  const [numberOfOfferRows, setNumberOfOfferRows] = useState<number>(
    Number(settings?.mainLayout.rowsNumber) || 1
  );

  useEffect(() => {
    setNumberOfOfferColumns(Number(settings?.mainLayout.offersNumber) || 3);
    setNumberOfOfferRows(Number(settings?.mainLayout.rowsNumber) || 1);
  }, [settings]);

  const updateFilteredOffers = (newOffers: Offer[], currentOffers: Offer[]) => {
    const currentOfferIds: string[] = currentOffers.map((i) => i.id);
    setOfferFilteredList(
      newOffers.filter((f) => currentOfferIds.includes(f.id))
    );
  };

  const featuredOffers = useMemo(() => {
    if (!settings?.mainLayout.showFeatured) return [];
    return offerFilteredList
      .filter((o) => o.slot <= -10)
      .sort((a, b) => b.slot - a.slot);
  }, [offerFilteredList, settings?.mainLayout.showFeatured]);

  const resolvedEmailForAutoSend = useMemo(() => {
    if (preEmail?.trim()) {
      return preEmail.trim();
    }
    if (email?.trim()) {
      return email.trim();
    }

    const cookieOptions = campaign?.cookieOptions || {
      allowCookies: false,
      allowSessionCookies: false,
      allowPersistentCookies: false,
      expiryDays: "1000",
    };
    return getStorageItem("brswEmail", cookieOptions)?.trim() || "";
  }, [campaign?.cookieOptions, email, preEmail]);

  const canAutoSendEmailFromLabel =
    campaign?.campaignGeneralOptions?.manualEmailEntry === false &&
    Boolean(resolvedEmailForAutoSend) &&
    EMAIL_REGEX.test(resolvedEmailForAutoSend);

  const canAutoSendEmailFromMainCTA =
    campaign?.campaignGeneralOptions?.autoSendEmail === true &&
    campaign?.campaignGeneralOptions?.manualEmailEntry === false &&
    Boolean(resolvedEmailForAutoSend) &&
    EMAIL_REGEX.test(resolvedEmailForAutoSend);

  const handleAutoSendFromOfferLabel = async (offer: Offer, slot: number) => {
    if (
      offer.offerSettings?.bundleOptions?.isBundled ||
      !resolvedEmailForAutoSend.trim() ||
      !campaign?.campaignGeneralOptions?.saveOfferOptions?.allowEmail
    ) {
      return;
    }

    setAutoSendingOfferId(offer.id);
    const emailParams: QuerySaveEmail = {
      offerid: offer.id,
      email: resolvedEmailForAutoSend,
      offerLink: offer.trackingUrl,
      companyId: campaign?.company_Id || "",
      placementId,
      campaignId,
      templateId: settingsTemplate.identifier,
      slot,
      orderId: offer.orderId,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      layoutId,
    };

    try {
      const response = await postTagEmailOffer(emailParams);
      getTrackingEvent({
        event: "onsendemailclick",
        retailerId: campaign?.company_Id,
        campaignId,
        offerId: offer.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });

      if (response?.data?.status) {
        updateStorageItem(
          "brswEmail",
          resolvedEmailForAutoSend,
          campaign?.cookieOptions
        );
        setPreEmail(resolvedEmailForAutoSend);
        setSelectedEmailOfferId("");
      }
    } finally {
      setAutoSendingOfferId("");
    }
  };

  const handleClose = () => {
    if (!settings?.mainLayout?.startAsExpanded) {
      setLoadMore(false);
    }
    setSelectedEmailOfferId("");

    setSingle(undefined);
    setOpen(false);
    campaign &&
      getTrackingEvent({
        event: "ontemplateclose",
        retailerId: campaign.company_Id,
        campaignId,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });
    setCampaign(undefined);
    window.top?.postMessage(
      { event: isExit ? "closeExitIframe" : "closeMainIframe", load: "" },
      "*"
    );
  };

  const handleClickSingleWrapper = (event: React.MouseEvent<HTMLElement>) => {
    if (
      event.target instanceof Node &&
      !refOffers.current?.contains(event.target)
    ) {
      handleClose();
    }
  };

  const handleCloseSingle = () => {
    setSingle(undefined);
    setSingleSave(false);
  };

  const checkSelectedOffers = (
    selectedOffers: string[] = [],
    _campaign: CampaignType
  ) => {
    const newOffers = _campaign.offers.map((offer: Offer) => {
      return { ...offer, isAdded: selectedOffers.includes(offer.id) };
    });
    updateFilteredOffers(newOffers, offerFilteredList);
  };

  const openSingle = (
    offer: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => {
    if (isAutoEmail && !single) {
      const params = {
        offer,
        isSave,
        isAuto: true,
        displayOnly,
        campaign: campaign,
        settings,
        companyId: campaign?.company_Id,
        campaignId: campaign?.id,
        placementId,
        placementName,
        slot,
      };
      !single &&
        window.top?.postMessage(
          { event: "emailPopupEmbedded", load: params },
          "*"
        );
    } else {
      setSingle({ ...offer, slot });
      setSingleSave(isSave);
      setSingleShare(isShare || false);
      window.top?.postMessage({ event: "openSingle", offer, slot }, "*");
    }
  };

  const handleAdd = (offer: Offer, value: boolean, slot: number) => {
    setSelectedOfferStorage({
      id: offer.id,
      link: offer.trackingUrl,
      active: value,
      layoutId: layoutId || "",
    });
    if (campaign) {
      const newOffers: Offer[] = campaign.offers.map((_offer: Offer) => {
        if (_offer.id === offer.id) {
          _offer.isAdded = value;
        }
        return _offer;
      });
      setCampaign(
        (prevState) => prevState && { ...prevState, offers: newOffers }
      );

      updateFilteredOffers(newOffers, offerFilteredList);
    } else {
      const _single = { ...single, isAdded: value };
      setSingle((prevState) => prevState && { ...prevState, isAdded: value });
      window.top?.postMessage(
        { event: "singleChange", offer: _single, slot },
        "*"
      );
    }

    value &&
      !single &&
      campaign &&
      getTrackingEvent({
        event: "onselectoffer",
        offerId: offer.id,
        retailerId: campaign.company_Id,
        campaignId: campaign.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });
  };

  const handleSelectAll = () => {
    if (!campaign) {
      return;
    }

    const selectedLength = offerFilteredList.filter(
      (o) => o.isAdded && !o.tagBlocked?.isBlocked
    ).length;
    const offerLength = offerFilteredList.filter(
      (o) => !o.tagBlocked?.isBlocked
    ).length;
    const shouldRemove = selectedLength === offerLength;

    // Batch update all offers at once instead of individual handleAdd calls
    const offersToUpdate = offerFilteredList.filter(
      (offer) =>
        !offer.tagBlocked?.isBlocked &&
        (shouldRemove ? offer.isAdded : !offer.isAdded)
    );

    if (offersToUpdate.length === 0) {
      return;
    }

    // Update campaign state with all changes at once
    const newOffers: Offer[] = campaign.offers.map((offer: Offer) => {
      const shouldUpdate = offersToUpdate.some((o) => o.id === offer.id);
      if (shouldUpdate) {
        return { ...offer, isAdded: !shouldRemove };
      }
      return offer;
    });

    setCampaign(
      (prevState) => prevState && { ...prevState, offers: newOffers }
    );
    updateFilteredOffers(newOffers, offerFilteredList);

    // Batch send storage updates for all offers using setSelectedOffersStorage
    const storageUpdates: SelectedOfferStorage[] = offersToUpdate.map(
      (offer) => ({
        id: offer.id,
        link: offer.trackingUrl,
        active: !shouldRemove,
        layoutId: layoutId || "",
      })
    );

    // Send all storage updates at once using the new batch method
    setSelectedOffersStorage(storageUpdates);

    // Batch tracking events for selected offers (only when adding, not removing)
    if (!shouldRemove && campaign) {
      offersToUpdate.forEach((offer) => {
        getTrackingEvent({
          event: "onselectoffer",
          offerId: offer.id,
          retailerId: campaign.company_Id,
          campaignId: campaign.id,
          placementId,
          placementName,
          tid: settingsTemplate.identifier,
          slot: offer.slot,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          pltid: layoutId,
        });
      });
    }
  };

  const setSelectedOfferStorage = (storage: SelectedOfferStorage) => {
    window.top?.postMessage({ event: "setSelectedOffer", load: storage }, "*");
  };

  const setSelectedOffersStorage = (storage: SelectedOfferStorage[]) => {
    window.top?.postMessage({ event: "setSelectedOffers", load: storage }, "*");
  };

  useEffect(() => {
    if (defaultOpen) {
      setCampaign(campaignRes);
      if (campaignRes && !single) {
        if (hasOpen) {
          getTrackingEvent({
            event: "onmainbuttonclick",
            retailerId: campaignRes?.company_Id,
            campaignId,
            placementId,
            placementName,
            tid: settingsTemplate.identifier,
            pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
            pltid: layoutId,
          });
        }
        getTrackingEvent({
          event: "ontemplateopen",
          retailerId: campaignRes.company_Id,
          campaignId: campaignRes.id,
          placementId,
          placementName,
          tid: settingsTemplate.identifier,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          pltid: layoutId,
        });
      }
    }
    setHasOpen(true);
    // eslint-disable-next-line
  }, [defaultOpen]);

  useEffect(() => {
    checkSelectedOffers(selectedOffers, campaignRes);
    // eslint-disable-next-line
  }, [selectedOffers, campaignRes]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        !single && handleClose();
      }
    };
    const iframeWindow = window;
    const iframeDocument = document;
    iframeDocument.addEventListener("keydown", handleEscKey);
    iframeWindow.addEventListener("keydown", handleEscKey);

    !single && iframeWindow.focus();

    return () => {
      iframeDocument.removeEventListener("keydown", handleEscKey);
      iframeWindow.removeEventListener("keydown", handleEscKey);
    };
    // eslint-disable-next-line
  }, [defaultOpen, single]);

  const renderFeaturedOffers = () => {
    if (
      campaign &&
      settings &&
      featuredOffers.length > 0 &&
      settings?.mainLayout.showFeatured
    ) {
      if (device === LayoutDevices.mobile) {
        return (
          <MultipleFeaturedWrapperMobile
            position={settings?.mainLayout?.featurePosition || "top"}
            featuredOffers={featuredOffers}
            settings={settings}
            settingsTemplate={settingsTemplate}
            displayOnly={displayOnly}
            campaignId={campaignId}
            placementId={placementId}
            placementName={placementName}
            preEmail={preEmail || email}
            setPreEmail={setPreEmail}
            campaign={campaign}
            openSingle={openSingle}
            onAdd={handleAdd}
            layoutId={layoutId}
            canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
            canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
            onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
            autoSendingOfferId={autoSendingOfferId}
          />
        );
      }
      if (featuredOffers.length > 1) {
        return (
          <MultipleFeaturedWrapperDesktop
            featuredOffers={featuredOffers}
            settings={settings}
            settingsTemplate={settingsTemplate}
            displayOnly={displayOnly}
            campaignId={campaignId}
            placementId={placementId}
            placementName={placementName}
            preEmail={preEmail || email}
            setPreEmail={setPreEmail}
            campaign={campaign}
            openSingle={openSingle}
            onAdd={handleAdd}
            layoutId={layoutId}
            canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
            canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
            onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
            autoSendingOfferId={autoSendingOfferId}
          />
        );
      } else {
        return featuredOffers.map((featuredOffer) => (
          <div className="super-desktop-feature" key={featuredOffer.id}>
            {!settings?.mainLayout?.featuredSize ||
              settings?.mainLayout?.featuredSize === "large" ? (
              <FeaturedOfferLarge
                slot={featuredOffer.slot}
                settingsTemplate={settingsTemplate}
                mainSettings={settings?.mainLayout}
                campaignId={campaignId}
                placementId={placementId}
                placementName={placementName}
                offer={featuredOffer}
                offerLength={campaign.offers.length}
                settings={settings?.dealLayout}
                defaultOpen={defaultOpen}
                sessionId={sessionId}
                openSingle={openSingle}
                onAdd={handleAdd}
                setPreEmail={setPreEmail}
                preEmail={preEmail || email}
                displayOnly={displayOnly}
                campaign={campaign}
                isAdded={featuredOffer.isAdded || false}
                retailerId={campaign.company_Id}
                hasVoucher={
                  (featuredOffer.voucher &&
                    !!featuredOffer.voucher.voucherType) ||
                  false
                }
                otherSettings={settings.otherSettings}
                layoutId={layoutId}
                layoutOptions={settings.layoutOptions}
                canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
                autoSendingOfferId={autoSendingOfferId}
              />
            ) : (
              <FeaturedOfferSmall
                slot={featuredOffer.slot}
                settingsTemplate={settingsTemplate}
                mainSettings={settings?.mainLayout}
                campaignId={campaignId}
                placementId={placementId}
                placementName={placementName}
                offer={featuredOffer}
                offerLength={campaign.offers.length}
                settings={settings?.dealLayout}
                defaultOpen={defaultOpen}
                sessionId={sessionId}
                openSingle={openSingle}
                onAdd={handleAdd}
                setPreEmail={setPreEmail}
                preEmail={preEmail || email}
                displayOnly={displayOnly}
                campaign={campaign}
                isAdded={featuredOffer.isAdded || false}
                retailerId={campaign.company_Id}
                hasVoucher={
                  (featuredOffer.voucher &&
                    !!featuredOffer.voucher.voucherType) ||
                  false
                }
                otherSettings={settings.otherSettings}
                layoutId={layoutId}
                layoutOptions={settings.layoutOptions}
                canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
                autoSendingOfferId={autoSendingOfferId}
              />
            )}
          </div>
        ));
      }
    }
  };

  const featuredPosition = settings?.mainLayout?.featurePosition || "top";

  const svgAnimationKey = [
    settings?.mainLayout?.iconAnimation,
    settings?.mainLayout?.iconAnimationType,
    settings?.mainLayout?.iconAnimationRepeat,
  ].join("-");

  const svgAnimationStyle = settings?.mainLayout?.iconAnimation
    ? {
      animation: `${settings?.mainLayout?.iconAnimationType} 1s linear`,
      animationIterationCount: settings?.mainLayout?.iconAnimationRepeat,
      display: "inline-flex",
    }
    : {};
  const resolvedEmailForBanner =
    preEmail || email || params?.contactDetails?.[0]?.email;
  const headerEmailAutomaticTermsText = getHeaderEmailAutomaticTermsText(
    settings?.mainLayout,
    campaign?.campaignGeneralOptions,
    settingsTemplate.identifier
  );

  return (
    <>
      <div
        className={`super-desktop-wrapper ${settings?.location.location} swiper-desktop-wrapper-${settings?.mainLayout?.offersNumber || 3}`}
        onClick={handleClickSingleWrapper}
        tabIndex={0}
        role="region"
        aria-label="Brandswap offers and promotions"
      >
        <div
          className={`super-desktop-popup super-wrapper-${device}
             ${!featuredOffers.length
              ? `swiper-container-${settings?.mainLayout?.offersNumber || 3}`
              : "swiper-container-featured"
            }
              ${campaignRes.offers.length < 3
              ? "super-desktop-popup-" + campaignRes.offers.length
              : "super-desktop-popup-sixpack"
            }`}
          ref={refOffers}
          style={{
            backgroundColor: settings?.mainLayout.backgroundColor,
            borderColor:
              settings?.mainLayout?.borderColor ||
              settings?.mainLayout.backgroundColor,
            borderRadius: `${settings?.mainLayout.borderRadius}px`,
            transform: `translate(${settings?.location.x || 0}px, ${settings?.location.y || 0
              }px)`,
            fontFamily: settings?.mainLayout.fontFamily,
            color: settings?.mainLayout.textColor,
            padding: `${typeof settings?.mainLayout.paddingTopBottom !== "undefined"
              ? settings?.mainLayout.paddingTopBottom
              : 20
              }px ${typeof settings?.mainLayout.paddingLeftRight !== "undefined"
                ? settings?.mainLayout.paddingLeftRight
                : 35
              }px`,
          }}
        >
          <div className={`super-desktop-title`}>
            {settings?.mainLayout?.headerIcon === "gift" && (
              <>
                <div
                  className="super-desktop-title-icon"
                  key={svgAnimationKey}
                  style={svgAnimationStyle}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.2 10V19H2.8V10M10 19V5.5M10 5.5H5.95C5.35326 5.5 4.78097 5.26295 4.35901 4.84099C3.93705 4.41903 3.7 3.84674 3.7 3.25C3.7 2.65326 3.93705 2.08097 4.35901 1.65901C4.78097 1.23705 5.35326 1 5.95 1C9.1 1 10 5.5 10 5.5ZM10 5.5H14.05C14.6467 5.5 15.219 5.26295 15.641 4.84099C16.0629 4.41903 16.3 3.84674 16.3 3.25C16.3 2.65326 16.0629 2.08097 15.641 1.65901C15.219 1.23705 14.6467 1 14.05 1C10.9 1 10 5.5 10 5.5ZM1 5.5H19V10H1V5.5Z"
                      stroke={settings?.mainLayout.headerTextColor}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </>
            )}

            {settings?.mainLayout?.headerIcon === "lock" && (
              <>
                <div
                  className="super-desktop-title-icon"
                  key={svgAnimationKey}
                  style={svgAnimationStyle}
                >
                  {displayOnly ? (
                    <svg
                      width="42"
                      height="47"
                      viewBox="0 0 42 47"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.88889 21.25V12.25C9.88889 9.26631 11.0595 6.40483 13.1433 4.29505C15.227 2.18526 18.0532 1 21 1C23.9469 1 26.773 2.18526 28.8567 4.29505C30.9405 6.40483 32.1111 9.26631 32.1111 12.25V21.25M5.44444 21.25H36.5556C39.0102 21.25 41 23.2647 41 25.75V41.5C41 43.9853 39.0102 46 36.5556 46H5.44444C2.98985 46 1 43.9853 1 41.5V25.75C1 23.2647 2.98985 21.25 5.44444 21.25Z"
                        stroke={settings?.mainLayout.headerTextColor}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="42"
                      height="47"
                      viewBox="0 0 42 47"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.88889 21.2554V12.258C9.88613 9.46896 10.9073 6.77834 12.7542 4.7085C14.601 2.63865 17.1419 1.33726 19.8834 1.05697C22.6248 0.776667 25.3714 1.53746 27.5899 3.19164C29.8084 4.84583 31.3406 7.2754 31.8889 10.0087M5.44444 21.2572H36.5556C39.0102 21.2572 41 23.2714 41 25.7559V41.5013C41 43.9859 39.0102 46 36.5556 46H5.44444C2.98985 46 1 43.9859 1 41.5013V25.7559C1 23.2714 2.98985 21.2572 5.44444 21.2572Z"
                        stroke={settings?.mainLayout.headerTextColor}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </>
            )}

            {settings?.mainLayout?.headerIcon === "custom" &&
              settings?.mainLayout?.headerCustomIcon && (
                <div
                  className="super-desktop-title-icon"
                  key={svgAnimationKey}
                  style={svgAnimationStyle}
                  dangerouslySetInnerHTML={{
                    __html: settings.mainLayout.headerCustomIcon,
                  }}
                />
              )}

            <span
              className={`super-desktop-title-label`}
              style={{
                fontSize: settings?.mainLayout.headerFontSize + "px",
                fontWeight: settings?.mainLayout.headerFontWeight,
                color: settings?.mainLayout.headerTextColor,
              }}
              role="contentinfo"
              tabIndex={0}
              aria-label={settings?.mainLayout.headerText || ""}
            >
              {settings?.mainLayout.headerText || ""}
            </span>
          </div>

          <div
            className={`super-desktop-description`}
            style={{
              fontSize: settings?.mainLayout.subHeaderFontSize + "px",
              fontWeight: settings?.mainLayout.subHeaderFontWeight,
              color: settings?.mainLayout.subHeaderTextColor,
            }}
            role="contentinfo"
            tabIndex={0}
            aria-label={settings?.mainLayout.subHeaderText || ""}
          >
            {settings?.mainLayout.subHeaderText || ""}
          </div>

          <EmailAutomaticTermsBanner
            campaignGeneralOptions={campaign?.campaignGeneralOptions}
            cookieOptions={campaign?.cookieOptions}
            text={headerEmailAutomaticTermsText}
            email={resolvedEmailForBanner}
            icon={settings?.dealLayout?.iconInfo?.value}
            onEmailChange={setPreEmail}
            mobile={device === LayoutDevices.mobile}
          />

          {settings?.filters?.filters && campaign && (
            <TemplateFilters
              campaign={{
                ...campaign,
                offers: campaign.offers.map((o: Offer) => ({
                  ...o,
                  isAdded: selectedOffers.includes(o.id),
                })),
              }}
              settings={settings}
              setFilteredList={setOfferFilteredList}
            />
          )}

          {displayOnly && campaign?.campaignGeneralOptions?.showAddToOrder && (
            <PreCountAndSelectAll
              offerLength={
                offerFilteredList.filter((o) => !o.tagBlocked?.isBlocked).length
              }
              countText={settings?.otherSettings?.counterText}
              selectText={settings?.otherSettings?.selectedText}
              showCheckboxSelect={
                settings?.otherSettings?.showCheckboxSelect || false
              }
              showSelectAll={settings?.otherSettings?.showSelectAll || false}
              showCount={settings?.otherSettings?.showCounter || false}
              noOfSelected={
                offerFilteredList.filter(
                  (o) => o.isAdded && !o.tagBlocked?.isBlocked
                ).length
              }
              handleSelectAll={handleSelectAll}
              highlightColor={settings?.mainLayout?.highlightColor}
              borderColor={settings?.dealLayout?.borderColor}
            />
          )}

          {featuredPosition &&
            featuredPosition === "top" &&
            renderFeaturedOffers()}

          {(isLoadMore || settings?.mainLayout?.startAsExpanded) &&
            settings?.mainLayout?.rowsNumber === "2" &&
            offerFilteredList.length >
            Number(settings.mainLayout.offersNumber) && (
              <div
                className={`super-desktop-swiper swiper-column-${settings.mainLayout.offersNumber || 3
                  }`}
                style={
                  {
                    "--swiper-pagination-color": settings?.mainLayout.textColor,
                    "--swiper-pagination-bullet-inactive-color": `${settings?.mainLayout.textColor}e0`,
                  } as CSSProperties
                }
              >
                {campaign && (
                  <SwiperComponent
                    key={`static-swiper-${isLoadMore ? "expanded" : "collapsed"
                      }`}
                    disabled={true}
                    offers={
                      settings?.mainLayout.showFeatured
                        ? offerFilteredList
                          .filter((f) => f.slot > -10)
                          .slice(
                            0,
                            Number(settings.mainLayout.offersNumber) || 3
                          )
                        : offerFilteredList.slice(
                          0,
                          Number(settings.mainLayout.offersNumber) || 3
                        )
                    }
                    settings={settings}
                    campaign={campaign}
                    defaultOpen={defaultOpen}
                    settingsTemplate={settingsTemplate}
                    displayOnly={displayOnly}
                    sessionId={sessionId}
                    campaignId={campaignId}
                    placementId={placementId}
                    placementName={placementName}
                    openSingle={openSingle}
                    onAdd={handleAdd}
                    setPreEmail={setPreEmail}
                    preEmail={preEmail || email}
                    selectedEmailOfferId={selectedEmailOfferId}
                    setSelectedEmailOfferId={setSelectedEmailOfferId}
                    layoutId={layoutId}
                    numberOfOfferColumns={numberOfOfferColumns}
                    numberOfOfferRows={numberOfOfferRows}
                    rowNumber={numberOfOfferRows === 1 ? 1 : isLoadMore ? 1 : 2}
                    isLoadMore={isLoadMore}
                    isSeen={isSeen}
                    canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                    canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                    onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
                    autoSendingOfferId={autoSendingOfferId}
                  ></SwiperComponent>
                )}
              </div>
            )}

          {featuredPosition &&
            featuredPosition === "middle" &&
            renderFeaturedOffers()}

          <div
            className={`super-desktop-swiper 
              swiper-column-${settings.mainLayout.offersNumber || 3}`}
            style={
              {
                "--swiper-pagination-color": settings?.mainLayout.textColor,
                "--swiper-pagination-bullet-inactive-color": `${settings?.mainLayout.textColor}e0`,
              } as CSSProperties
            }
          >
            {campaign && (
              <SwiperComponent
                disabled={
                  false
                  // !(
                  //   (isLoadMore || settings?.mainLayout?.startAsExpanded) &&
                  //   settings?.mainLayout?.rowsNumber === "2" &&
                  //   offerFilteredList.length >
                  //     Number(settings.mainLayout.offersNumber)
                  // )
                }
                offers={
                  settings?.mainLayout.showFeatured
                    ? offerFilteredList
                      .filter((f) => f.slot > -10)
                      .slice(
                        (isLoadMore ||
                          settings?.mainLayout?.startAsExpanded) &&
                          settings?.mainLayout?.rowsNumber === "2" &&
                          offerFilteredList.length >
                          Number(settings.mainLayout.offersNumber)
                          ? Number(settings.mainLayout.offersNumber) || 3
                          : 0,
                        isLoadMore ||
                          settings?.mainLayout?.startAsExpanded ||
                          settings?.mainLayout?.rowsNumber === "1"
                          ? offerFilteredList.length
                          : Number(settings.mainLayout.offersNumber) || 3
                      )
                    : offerFilteredList.slice(
                      (isLoadMore || settings?.mainLayout?.startAsExpanded) &&
                        settings?.mainLayout?.rowsNumber === "2" &&
                        offerFilteredList.length >
                        Number(settings.mainLayout.offersNumber)
                        ? Number(settings.mainLayout.offersNumber) || 3
                        : 0,
                      isLoadMore ||
                        settings?.mainLayout?.startAsExpanded ||
                        settings?.mainLayout?.rowsNumber === "1"
                        ? offerFilteredList.length
                        : Number(settings.mainLayout.offersNumber) || 3
                    )
                }
                settings={settings}
                campaign={campaign}
                defaultOpen={defaultOpen}
                settingsTemplate={settingsTemplate}
                displayOnly={displayOnly}
                campaignId={campaignId}
                placementId={placementId}
                placementName={placementName}
                openSingle={openSingle}
                onAdd={handleAdd}
                sessionId={sessionId}
                setPreEmail={setPreEmail}
                preEmail={preEmail || email}
                selectedEmailOfferId={selectedEmailOfferId}
                setSelectedEmailOfferId={setSelectedEmailOfferId}
                layoutId={layoutId}
                numberOfOfferColumns={numberOfOfferColumns}
                numberOfOfferRows={numberOfOfferRows}
                rowNumber={numberOfOfferRows === 1 ? 1 : isLoadMore ? 2 : 1}
                isLoadMore={isLoadMore}
                isSeen={isSeen}
                canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
                autoSendingOfferId={autoSendingOfferId}
              ></SwiperComponent>
            )}
          </div>

          {!isLoadMore &&
            settings?.mainLayout.rowsNumber === "2" &&
            offerFilteredList.length - (featuredOffers.length || 0) >
            Number(settings.mainLayout.offersNumber) && (
              <div className="swiper-load-more">
                <div
                  className="button-load-more"
                  onClick={() => setLoadMore(true)}
                  tabIndex={0}
                  role="button"
                  aria-label="Show more offers"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setLoadMore(true);
                    }
                  }}
                  style={{
                    backgroundColor:
                      settings?.showMore?.backgroundColor || "#383838",
                    color: settings?.showMore?.textColor || "#ffffff",
                    fontWeight: settings?.showMore?.fontWeight,
                    borderRadius: `${settings?.showMore?.rounding}px`,
                    border: `1px solid ${settings?.showMore?.borderColor ||
                      settings?.showMore?.backgroundColor
                      }`,
                  }}
                >
                  {settings?.showMore?.input || "Show more offers"}
                </div>
              </div>
            )}

          {featuredPosition &&
            featuredPosition === "bottom" &&
            renderFeaturedOffers()}

          <RedirectionButton settings={settings} displayOnly={displayOnly} />

          <div className="super-desktop-footer">
            {displayOnly ? (
              <>
                {campaign?.campaignGeneralOptions.emailCaptureOnOffers && (
                  <>
                    {!settings?.dealLayout?.icons ||
                      settings?.dealLayout?.icons === TemplateIcons.default ? (
                      <TemplateIcon
                        icon={
                          settings?.dealLayout?.iconInfo?.value ||
                          ICONS_DEFAULT_VALUES.iconInfo
                        }
                        color={settings?.mainLayout.textColor || "#000"}
                      />
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                          fill={settings?.mainLayout.textColor}
                        />
                      </svg>
                    )}
                    <FooterText
                      displayOnly={displayOnly}
                      footerText={settings?.mainLayout.footerText}
                      mobile={device === LayoutDevices.mobile}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                {!settings?.dealLayout?.icons ||
                  settings?.dealLayout?.icons === TemplateIcons.default ? (
                  <TemplateIcon
                    icon={
                      settings?.dealLayout?.iconInfo?.value ||
                      ICONS_DEFAULT_VALUES.iconInfo
                    }
                    color={settings?.mainLayout.textColor || "#000"}
                  />
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                      fill={settings?.mainLayout.textColor}
                    />
                  </svg>
                )}
                <FooterText
                  displayOnly={displayOnly}
                  footerText={settings?.mainLayout.footerText}
                  mobile={device === LayoutDevices.mobile}
                />
              </>
            )}
          </div>

          {/* Close button order last for accessibility tab index */}
          <div
            className="super-desktop-minimize"
            onClick={() => handleClose()}
            tabIndex={0}
            role="button"
            aria-label="Close BrandSwap offers popup"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleClose();
              }
            }}
            style={{
              backgroundColor: settings?.dealLayout.backgroundColor,
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.176625 0.34468C0.385958 0.133389 0.710902 0.109912 0.945926 0.274249L1.02944 0.34468L7.41698 6.79213L13.9706 0.178277C14.2061 -0.0594256 14.5879 -0.0594256 14.8234 0.178277C15.0327 0.389568 15.056 0.717552 14.8932 0.954775L14.8234 1.03907L8.2698 7.65293L14.5195 13.9609C14.755 14.1986 14.755 14.584 14.5195 14.8217C14.3102 15.033 13.9852 15.0565 13.7502 14.8922L13.6667 14.8217L7.41698 8.51372L1.17826 14.8118C0.942765 15.0495 0.560944 15.0495 0.325445 14.8118C0.116112 14.6005 0.0928526 14.2726 0.255667 14.0353L0.325445 13.951L6.56416 7.65293L0.176625 1.20548C-0.0588749 0.967774 -0.0588749 0.582382 0.176625 0.34468Z"
                fill={settings?.dealLayout.textColor}
              />
            </svg>
          </div>
        </div>
      </div>
      {single && campaign?.id && (
        <TemplateSingle
          slot={single.slot}
          settings={settings}
          isInline={false}
          offer={single}
          isSave={isSingleSave}
          sessionId={sessionId}
          isShare={isSingleShare}
          placementId={placementId}
          placementName={placementName}
          settingsTemplate={settingsTemplate}
          campaignObj={campaign}
          displayOnly={displayOnly}
          campaignId={campaign?.id}
          companyId={campaign.company_Id}
          updateCampaign={handleAdd}
          setPreEmail={setPreEmail}
          preEmail={preEmail || email}
          closeSignle={handleCloseSingle}
          layoutId={layoutId}
        />
      )}
    </>
  );
};

export default PreviewPopupSuper;
